/**
 * useReceivableMutations - Extracted from useAccountsReceivable
 * 
 * Hook isolado contendo APENAS as mutations de contas a receber.
 * Não dispara nenhuma query, evitando fetches duplicados quando
 * componentes precisam apenas de ações (delete, renegotiate, etc).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { AccountReceivable } from '@/types/financial';
import { useToast } from '@/hooks/use-toast';
import { useMultiTenant } from '@/hooks/useMultiTenant';
import { useAuth } from '@/contexts/AuthContext';
import { format } from 'date-fns';
import { auditService } from '@/services/auditService';
import { translateSupabaseError } from '@/utils/errorTranslation';
import { formatCurrency } from '@/utils/formatters';
import { assertPaymentMethodSlug } from '@/utils/paymentMethodGuard';

/** Standard invalidation keys for receivable mutations */
const RECEIVABLE_QUERY_KEYS = [
  ['accounts-receivable'],
  ['receivables-stats'],
  ['financial-dashboard'],
  ['financial-accounts'],
] as const;

const FULL_INVALIDATION_KEYS = [
  ...RECEIVABLE_QUERY_KEYS,
  ['sales'],
] as const;

/** I.154: mesmo texto da recusa de soft_delete_receivable para parcela de venda */
export const SALE_PARCEL_DELETE_MESSAGE =
  'Parcela de venda não pode ser excluída em Contas a Receber. Para corrigir as parcelas, use Reparcelar na venda; para tirar a venda, exclua a venda.';

export const useReceivableMutations = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { companyId } = useMultiTenant();
  const { user } = useAuth();

  const invalidateAll = (extraKeys: string[][] = []) => {
    RECEIVABLE_QUERY_KEYS.forEach(key => 
      queryClient.invalidateQueries({ queryKey: key as unknown as string[] })
    );
    extraKeys.forEach(key => 
      queryClient.invalidateQueries({ queryKey: key })
    );
  };

  // ====================================
  // CREATE
  // ====================================
  const createMutation = useMutation({
    mutationFn: async (receivable: Partial<AccountReceivable>) => {
      if (!companyId) throw new Error('Company ID não disponível. Faça login novamente.');
      assertPaymentMethodSlug(receivable.payment_method);

      const { is_received, ...cleanReceivable } = receivable as any;
      const wantsPaid = Boolean(is_received) || cleanReceivable.status === 'paid';
      if (wantsPaid && !cleanReceivable.financial_account_id) throw new Error('Conta financeira obrigatória para conta já recebida');

      const { data: numberData } = await supabase.rpc('generate_next_receivable_number');

      // R-P8.5: título nasce pending; a baixa é da RPC (dona única)
      const mappedReceivable = {
        ...cleanReceivable,
        company_id: companyId,
        transaction_number: numberData,
        status: 'pending',
        payment_date: null,
        paid_value: 0,
        // Set after spread to guarantee created_by is never overridden by caller
        created_by: user?.id || null,
      };

      const { data, error } = await supabase
        .from('accounts_receivable')
        .insert([mappedReceivable as any])
        .select()
        .single();

      if (error) throw error;

      if (wantsPaid) {
        const paymentDate = cleanReceivable.payment_date || data.due_date || new Date().toISOString().split('T')[0];
        const { data: rpc, error: rpcError } = await supabase.rpc('register_manual_receivable_payment', {
          p_receivable_id: data.id,
          p_payment_value: Number(data.total_value) - Number(data.discount_value || 0),
          p_payment_date: paymentDate,
          p_payment_method: data.payment_method || 'pix',
          p_financial_account_id: data.financial_account_id,
          p_notes: 'Pagamento registrado automaticamente na criação',
        });
        if (rpcError) throw rpcError;
        const result = rpc as { status_after: string; paid_value_after: number };
        return {
          ...data,
          status: result.status_after,
          paid_value: result.paid_value_after,
          payment_date: paymentDate,
        } as AccountReceivable;
      }

      return data as AccountReceivable;
    },
    onSuccess: async (data) => {
      invalidateAll([['sales'], ['unified-sales'], ['accounts-receivable-by-sale']]);

      await auditService.logAction({
        action: 'CRIAÇÃO',
        module: 'FINANCEIRO',
        entityType: 'conta_receber',
        entityId: data.id,
        description: `Conta a receber criada: ${data.transaction_number}`,
        details: {
          transaction_number: data.transaction_number,
          customer_id: data.customer_id,
          total_value: data.total_value,
          due_date: data.due_date,
          description: data.description,
        },
      });

      toast({ title: 'Conta a receber criada com sucesso' });
    },
    onError: (error) => {
      toast({ title: 'Erro ao criar conta a receber', description: translateSupabaseError(error), variant: 'destructive' });
    },
  });

  // ====================================
  // UPDATE
  // ====================================
  const updateMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<AccountReceivable> & { id: string }) => {
      assertPaymentMethodSlug(updates.payment_method);
      const { data, error } = await supabase
        .from('accounts_receivable')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;

      // Propagar vencimento/valor à parcela vinculada: create_receivables_from_sale
      // reconstrói o AR a partir de sale_installments — sem isso a edição manual
      // é revertida no próximo re-sync (edição da venda, entrega, faturamento).
      if (data.sale_installment_id && (updates.due_date !== undefined || updates.original_value !== undefined)) {
        const installmentUpdates: { due_date?: string; amount?: number } = {};
        if (updates.due_date !== undefined) installmentUpdates.due_date = updates.due_date;
        if (updates.original_value !== undefined) installmentUpdates.amount = updates.original_value;
        const { data: installmentRows, error: installmentError } = await supabase
          .from('sale_installments')
          .update(installmentUpdates)
          .eq('id', data.sale_installment_id)
          .select('id');
        if (installmentError) throw installmentError;
        if (!installmentRows || installmentRows.length === 0) {
          throw new Error('Parcela da venda vinculada a este título não foi atualizada (0 linhas afetadas)');
        }
      }
      return data;
    },
    onSuccess: async (updatedData) => {
      invalidateAll([['sales'], ['unified-sales'], ['accounts-receivable-by-sale']]);

      await auditService.logAction({
        action: 'ATUALIZAÇÃO',
        module: 'FINANCEIRO',
        entityType: 'conta_receber',
        entityId: updatedData.id,
        description: `Conta a receber atualizada: ${updatedData.transaction_number}`,
        details: {
          transaction_number: updatedData.transaction_number,
          customer_id: updatedData.customer_id,
          total_value: updatedData.total_value,
          status: updatedData.status,
        },
      });

      toast({ title: 'Conta a receber atualizada com sucesso' });
    },
    onError: (error) => {
      toast({ title: 'Erro ao atualizar conta a receber', description: translateSupabaseError(error), variant: 'destructive' });
    },
  });

  // ====================================
  // RECORD PAYMENT
  // ====================================
  const recordPaymentMutation = useMutation({
    mutationFn: async (data: {
      receivableId: string;
      paymentDate: string;
      paymentValue: number;
      paymentMethod: string;
      financialAccountId?: string;
      interestValue: number;
      fineValue: number;
      discountValue: number;
      feeValue: number;
      notes?: string;
    }) => {
      assertPaymentMethodSlug(data.paymentMethod);

      // P8.2 (§I.147-quater): RPC dona única da baixa — valida, insere o payment
      // e recompõe o título numa transação
      const { data: rpc, error: rpcError } = await supabase.rpc('register_manual_receivable_payment', {
        p_receivable_id: data.receivableId,
        p_payment_value: data.paymentValue,
        p_payment_date: data.paymentDate,
        p_payment_method: data.paymentMethod,
        p_financial_account_id: data.financialAccountId ?? undefined,
        p_interest_value: data.interestValue,
        p_fine_value: data.fineValue,
        p_discount_value: data.discountValue,
        p_fee_value: data.feeValue,
        p_notes: data.notes ?? undefined,
      });
      if (rpcError) throw rpcError;
      const newStatus = (rpc as { status_after: string }).status_after;
      const paymentId = (rpc as { payment_id: string }).payment_id;

      const { data: activePixCharge } = await supabase
        .from('pix_charge_history')
        .select('id, status')
        .eq('receivable_id', data.receivableId)
        .eq('status', 'ATIVA')
        .maybeSingle();

      if (activePixCharge) {
        const newPixStatus = data.paymentMethod === 'pix' ? 'CONCLUIDA' : 'EXPIRADA';
        await supabase
          .from('pix_charge_history')
          .update({ 
            status: newPixStatus,
            paid_at: data.paymentMethod === 'pix' ? new Date().toISOString() : null,
          })
          .eq('id', activePixCharge.id);
      }

      // fire-and-forget — auditService has internal try/catch; never blocks or throws
      // AUDIT-LOG-CHECK (I.160): o CHECK de audit_logs só aceita 4 tipos — a baixa vai como ATUALIZAÇÃO, evento em details
      auditService.logAction({
        action: 'ATUALIZAÇÃO',
        module: 'FINANCEIRO',
        entityType: 'accounts_receivable',
        entityId: data.receivableId,
        description: `Baixa de R$ ${data.paymentValue.toFixed(2)} via ${data.paymentMethod}`,
        details: {
          evento: 'baixa',
          payment_value: data.paymentValue,
          interest_value: data.interestValue,
          fine_value: data.fineValue,
          payment_method: data.paymentMethod,
          financial_account_id: data.financialAccountId,
          payment_date: data.paymentDate,
        },
      });

      return { newStatus, totalPayment: data.paymentValue, paymentId };
    },
    onSuccess: (data) => {
      invalidateAll([['sales'], ['unified-sales'], ['accounts-receivable-by-sale'], ['receivable-payments']]);
      toast({
        title: 'Recebimento registrado com sucesso',
        description: `Valor: ${formatCurrency(data.totalPayment)}`,
      });
    },
    onError: (error) => {
      toast({ title: 'Erro ao registrar recebimento', description: translateSupabaseError(error), variant: 'destructive' });
    },
  });

  // ====================================
  // RENEGOTIATE
  // ====================================
  const renegotiateMutation = useMutation({
    mutationFn: async (data: {
      receivableId: string;
      newDueDate: string;
      interestValue: number;
      fineValue: number;
      discountValue: number;
      paymentMethod?: string;
      financialAccountId?: string;
    }) => {
      const { data: receivable, error: fetchError } = await supabase
        .from('accounts_receivable')
        .select('original_value, total_value, paid_value, sale_installment_id')
        .eq('id', data.receivableId)
        .single();

      if (fetchError) throw fetchError;
      if (!receivable) throw new Error('Lançamento não encontrado');

      const outstandingBalance = (receivable.total_value || receivable.original_value) - (receivable.paid_value || 0);
      const newTotalValue = outstandingBalance + data.interestValue + data.fineValue - data.discountValue;

      const { error: updateError } = await supabase
        .from('accounts_receivable')
        .update({
          due_date: data.newDueDate,
          interest_value: data.interestValue + data.fineValue,
          discount_value: data.discountValue,
          total_value: newTotalValue,
          status: 'renegotiated',
          payment_method: data.paymentMethod,
          financial_account_id: data.financialAccountId,
        })
        .eq('id', data.receivableId)
        .select('id')
        .single();

      if (updateError) throw updateError;

      // A4.1 (§I.145 L4): propagar SOMENTE due_date à parcela vinculada —
      // gabarito Camada 1. Valor e status de parcela INTOCADOS (juros/multa/
      // desconto vivem no título; redesenho por decomposição = fila).
      // Título sem vínculo (avulso/PDV): zero propagação.
      if (receivable.sale_installment_id) {
        const { data: installmentRows, error: installmentError } = await supabase
          .from('sale_installments')
          .update({ due_date: data.newDueDate })
          .eq('id', receivable.sale_installment_id)
          .select('id');
        if (installmentError) throw installmentError;
        if (!installmentRows || installmentRows.length === 0) {
          throw new Error('Parcela da venda vinculada a este título não foi atualizada (0 linhas afetadas)');
        }
      }
      return { newDueDate: data.newDueDate, newTotalValue };
    },
    onSuccess: (data) => {
      invalidateAll([['sales'], ['unified-sales'], ['accounts-receivable-by-sale']]);
      toast({ 
        title: 'Lançamento renegociado com sucesso',
        description: `Nova data: ${format(new Date(data.newDueDate), 'dd/MM/yyyy')}`,
      });
    },
    onError: (error) => {
      toast({ title: 'Erro ao renegociar lançamento', description: translateSupabaseError(error), variant: 'destructive' });
    },
  });

  // ====================================
  // RENEGOTIATE BALANCE
  // ====================================
  const renegotiateBalanceMutation = useMutation({
    mutationFn: async (data: {
      receivableId: string;
      numberOfInstallments: number;
      firstDueDate: string;
      intervalDays: number;
    }) => {
      const { data: receivable, error: fetchError } = await supabase
        .from('accounts_receivable')
        .select('*')
        .eq('id', data.receivableId)
        .single();

      if (fetchError) throw fetchError;
      if (!receivable) throw new Error('Lançamento não encontrado');

      const balance = Math.max(0, (receivable.total_value || receivable.original_value) - (receivable.paid_value || 0));
      if (balance <= 0) throw new Error('Não há saldo para parcelar');

      const installmentValue = Math.floor((balance / data.numberOfInstallments) * 100) / 100;
      const lastInstallmentValue = balance - (installmentValue * (data.numberOfInstallments - 1));

      const newInstallments = [];
      for (let i = 0; i < data.numberOfInstallments; i++) {
        const { data: numberData, error: numberError } = await supabase.rpc('generate_next_receivable_number');
        if (numberError) throw numberError;

        const dueDate = new Date(data.firstDueDate);
        dueDate.setDate(dueDate.getDate() + (i * data.intervalDays));
        
        const value = i === data.numberOfInstallments - 1 ? lastInstallmentValue : installmentValue;
        
        newInstallments.push({
          company_id: receivable.company_id,
          transaction_number: numberData,
          customer_id: receivable.customer_id,
          sale_id: receivable.sale_id,
          rental_id: receivable.rental_id,
          origin_type: receivable.origin_type,
          origin_number: receivable.origin_number,
          description: `Renegociação - ${receivable.description}`,
          issue_date: format(new Date(), 'yyyy-MM-dd'),
          due_date: format(dueDate, 'yyyy-MM-dd'),
          original_value: value,
          total_value: value,
          paid_value: 0,
          interest_value: 0,
          discount_value: 0,
          status: 'pending',
          income_category_id: receivable.income_category_id,
          financial_account_id: receivable.financial_account_id,
          // A4.3 (§I.145 L4): títulos de renegociação nascem SEM
          // installment_number — número herdado colidiria com títulos
          // vivos da venda no par (sale_id, installment_number) e seria
          // adotável pelo fallback da regeneração v2 (analogia §I.88:
          // origin_number null nas parcelas do payable). A série vive em
          // total_installments/parent_transaction_id/notes.
          installment_number: null,
          total_installments: data.numberOfInstallments,
          parent_transaction_id: receivable.id,
          notes: `Parcela ${i + 1}/${data.numberOfInstallments} - Renegociação de ${receivable.transaction_number}`,
        });
      }

      const { error: insertError } = await supabase
        .from('accounts_receivable')
        .insert(newInstallments);

      if (insertError) throw insertError;

      const auditNote = `\n[${format(new Date(), 'dd/MM/yyyy HH:mm')}] Saldo de ${formatCurrency(balance)} renegociado em ${data.numberOfInstallments} parcelas.`;
      
      const { error: updateError } = await supabase
        .from('accounts_receivable')
        .update({
          status: 'renegotiated',
          notes: (receivable.notes || '') + auditNote,
        })
        .eq('id', data.receivableId);

      if (updateError) throw updateError;

      return { 
        balance, 
        numberOfInstallments: data.numberOfInstallments,
        originalTransactionNumber: receivable.transaction_number,
      };
    },
    onSuccess: (data) => {
      invalidateAll([['sales'], ['unified-sales'], ['accounts-receivable-by-sale']]);
      toast({ 
        title: 'Saldo renegociado com sucesso',
        description: `${formatCurrency(data.balance)} dividido em ${data.numberOfInstallments} parcelas`,
      });
    },
    onError: (error) => {
      toast({ title: 'Erro ao renegociar saldo', description: translateSupabaseError(error), variant: 'destructive' });
    },
  });

  // ====================================
  // REOPEN
  // ====================================
  const reopenMutation = useMutation({
    // ESTORNO-AUDITORIA (I.160, E3): motivo obrigatório; o banco guarda cada baixa no histórico de estornos
    mutationFn: async ({ id: receivableId, reason }: { id: string; reason: string }) => {
      const { data: receivable, error: fetchError } = await supabase
        .from('accounts_receivable')
        .select('*')
        .eq('id', receivableId)
        .single();

      if (fetchError) throw fetchError;
      if (!receivable) throw new Error('Lançamento não encontrado');

      const { data: result, error: rpcError } = await supabase.rpc('revert_receivable_payment', {
        p_receivable_id: receivableId,
        p_reason: reason,
      });

      if (rpcError) throw rpcError;
      return { ...receivable, revertResult: result };
    },
    onSuccess: async (receivable) => {
      invalidateAll([['sales'], ['bank-transactions'], ['unified-sales'], ['accounts-receivable-by-sale']]);

      await auditService.logAction({
        action: 'ATUALIZAÇÃO',
        module: 'FINANCEIRO',
        entityType: 'conta_receber',
        entityId: receivable.id,
        description: `Conta reaberta (estorno): ${receivable.transaction_number}`,
        details: {
          transaction_number: receivable.transaction_number,
          previous_status: receivable.status,
          previous_paid_value: receivable.paid_value,
        },
      });

      toast({ title: 'Recebimentos estornados' });
    },
    onError: (error) => {
      toast({ title: 'Erro ao estornar', description: translateSupabaseError(error), variant: 'destructive' });
    },
  });

  // ====================================
  // REVERT PAYMENT ENTRY (P8.4)
  // ====================================
  const revertPaymentEntryMutation = useMutation({
    // ESTORNO-AUDITORIA (I.160, E3): motivo obrigatório
    mutationFn: async ({ id: paymentId, reason }: { id: string; reason: string }) => {
      const { data: result, error: rpcError } = await supabase.rpc('revert_receivable_payment_entry', {
        p_payment_id: paymentId,
        p_reason: reason,
      });

      if (rpcError) throw rpcError;
      return result as { payment_id: string; receivable_id: string; principal_reverted: number };
    },
    onSuccess: async (result) => {
      invalidateAll([['sales'], ['bank-transactions'], ['unified-sales'], ['accounts-receivable-by-sale'], ['receivable-payments'], ['receivable-detail'], ['payment-reversals']]);

      await auditService.logAction({
        action: 'ATUALIZAÇÃO',
        module: 'FINANCEIRO',
        entityType: 'conta_receber',
        entityId: result.receivable_id,
        description: `Lançamento estornado: ${result.principal_reverted}`,
        details: result,
      });

      toast({ title: 'Lançamento estornado' });
    },
    onError: (error) => {
      toast({ title: 'Erro ao estornar lançamento', description: translateSupabaseError(error), variant: 'destructive' });
    },
  });

  // ====================================
  // DELETE (SOFT)
  // ====================================
  const deleteMutation = useMutation({
    mutationFn: async (receivableId: string) => {
      // Fetch receivable fields needed to check for active instruments
      const { data: rec, error: fetchErr } = await supabase
        .from('accounts_receivable')
        .select('id, sale_id, bank_slip_transaction_id, bank_slip_status, pix_txid, pix_expires_at, transaction_number')
        .eq('id', receivableId)
        .single();

      if (fetchErr || !rec) throw new Error('Lançamento não encontrado');

      // I.154 (decisão do Diretor 12/10): parcela de venda não se exclui aqui — recusa ANTES de
      // cancelar boleto/PIX (o banco também recusa em soft_delete_receivable).
      if (rec.sale_id) throw new Error(SALE_PARCEL_DELETE_MESSAGE);

      // Cancel active bank slip before deleting
      const recAny = rec as any;
      if (recAny.bank_slip_transaction_id && recAny.bank_slip_status && recAny.bank_slip_status !== 'cancelled') {
        const { data: res, error: fnErr } = await supabase.functions.invoke(
          'cancel-banking-slip',
          { body: { receivable_id: receivableId, motivoCancelamento: 'Exclusão pelo operador' } }
        );
        const payload = res as { success?: boolean; error?: string } | null;
        if (fnErr || !payload?.success) {
          const msg = payload?.error || (fnErr as any)?.message || 'Falha ao cancelar boleto';
          throw new Error(`Não foi possível excluir: ${msg}`);
        }
      }

      // Cancel active PIX before deleting
      if (recAny.pix_txid && recAny.pix_expires_at && new Date(recAny.pix_expires_at) > new Date()) {
        const { data: res, error: fnErr } = await supabase.functions.invoke(
          'cancel-pix-charge',
          { body: { receivable_id: receivableId, txid: recAny.pix_txid } }
        );
        const payload = res as { success?: boolean; error?: string } | null;
        if (fnErr || !payload?.success) {
          const msg = payload?.error || (fnErr as any)?.message || 'Falha ao cancelar PIX';
          throw new Error(`Não foi possível excluir: ${msg}`);
        }
      }

      const { data, error } = await supabase.rpc('soft_delete_receivable', {
        p_receivable_id: receivableId,
      });
      if (error) throw error;
      return data as { success: boolean; transaction_number: string; total_value: number; cascade_deleted_sale: boolean };
    },
    onSuccess: async (result) => {
      invalidateAll([['sales'], ['sales-stats'], ['unified-sales'], ['unified-sales-stats'], ['accounts-receivable-by-sale']]);
      
      const message = result?.cascade_deleted_sale 
        ? 'Lançamento e venda vinculada excluídos com sucesso'
        : 'Lançamento excluído com sucesso';
      
      toast({ title: message });
    },
    onError: (error) => {
      toast({ title: 'Erro ao excluir lançamento', description: translateSupabaseError(error), variant: 'destructive' });
    },
  });

  // ====================================
  // CREATE GROUP
  // ====================================
  const createReceivableGroupMutation = useMutation({
    mutationFn: async (groupData: {
      description: string;
      total_value: number;
      installments_count: number;
      group_type: 'installment' | 'recurrence';
      source_type: string;
      payment_method?: string;
      issue_date?: string;
      first_due_date: string;
      interval_days?: number;
      interval_months?: number;
      customer_id?: string;
      income_category_id?: string;
      cost_center_id?: string;
      financial_account_id?: string;
      invoice_number?: string;
      notes?: string;
      recurrence_frequency?: string;
      recurrence_occurrences?: number;
    }) => {
      if (!companyId) throw new Error('Company ID não disponível. Faça login novamente.');

      // R-P8.19 (E3): sem intervalo nenhum, lança — sem fallback silencioso.
      // interval_days em grupo mensal é redundância inerte (30×meses, round-trip
      // da edição); o cálculo de datas usa interval_months na RPC.
      let effectiveIntervalDays: number;
      if (groupData.interval_days != null) {
        effectiveIntervalDays = groupData.interval_days;
      } else if (groupData.interval_months != null) {
        effectiveIntervalDays = groupData.interval_months * 30;
      } else {
        throw new Error('Intervalo do grupo ausente');
      }

      let recurrenceTemplateId: string | null = null;

      if (groupData.group_type === 'recurrence' && groupData.recurrence_frequency) {
        const templateIntervalDays = effectiveIntervalDays;

        const { data: template, error: templateError } = await supabase
          .from('recurrence_templates')
          .insert({
            company_id: companyId,
            frequency: groupData.recurrence_frequency,
            interval_days: templateIntervalDays,
            start_date: groupData.first_due_date,
            next_run_date: groupData.first_due_date,
            active: true,
          })
          .select()
          .single();

        if (templateError) throw templateError;
        recurrenceTemplateId = template.id;
      }

      const { data: group, error: groupError } = await supabase
        .from('transaction_groups')
        .insert({
          company_id: companyId,
          group_type: groupData.group_type,
          source_type: groupData.source_type,
          description: groupData.description,
          total_value: groupData.total_value,
          installments_count: groupData.installments_count,
          payment_method: groupData.payment_method || null,
          recurrence_template_id: recurrenceTemplateId,
          customer_id: groupData.customer_id || null,
          income_category_id: groupData.income_category_id || null,
          cost_center_id: groupData.cost_center_id || null,
          financial_account_id: groupData.financial_account_id || null,
          invoice_number: groupData.invoice_number || null,
          notes: groupData.notes || null,
          issue_date: groupData.issue_date || format(new Date(), 'yyyy-MM-dd'),
          first_due_date: groupData.first_due_date,
          interval_days: effectiveIntervalDays,
          interval_months: groupData.interval_months ?? null,
        } as any)
        .select()
        .single();

      if (groupError) throw groupError;

      const { data: count, error: rpcError } = await supabase
        .rpc('generate_installments_from_group', { p_group_id: group.id });

      if (rpcError) throw rpcError;

      return { group, installmentsGenerated: count as number };
    },
    onSuccess: async (result) => {
      invalidateAll([['sales'], ['unified-sales'], ['accounts-receivable-by-sale']]);

      await auditService.logAction({
        action: 'CRIAÇÃO',
        module: 'FINANCEIRO',
        entityType: 'transaction_group',
        entityId: result.group.id,
        description: `Grupo de receitas criado: ${result.group.description} (${result.installmentsGenerated} parcelas)`,
        details: {
          group_id: result.group.id,
          total_value: result.group.total_value,
          installments_count: result.group.installments_count,
          group_type: result.group.group_type,
        },
      });

      toast({ title: `Grupo de receitas criado e ${result.installmentsGenerated} parcela(s) gerada(s) com sucesso` });
    },
    onError: (error) => {
      
      toast({ title: 'Erro ao criar grupo de receitas', description: translateSupabaseError(error), variant: 'destructive' });
    },
  });

  // ====================================
  // UPDATE GROUP (REGENERATE)
  // ====================================
  const updateReceivableGroupMutation = useMutation({
    mutationFn: async (params: {
      groupId: string;
      totalValue: number;
      installmentsCount: number;
      firstDueDate: string;
      intervalDays: number;
      description?: string;
      customerId?: string;
      incomeCategoryId?: string;
      costCenterId?: string;
      financialAccountId?: string;
      invoiceNumber?: string;
      notes?: string;
      paymentMethod?: string;
      issueDate?: string;
    }) => {
      const { data, error } = await supabase.rpc('update_receivable_group_and_regenerate', {
        p_group_id: params.groupId,
        p_total_value: params.totalValue,
        p_installments_count: params.installmentsCount,
        p_first_due_date: params.firstDueDate,
        p_interval_days: params.intervalDays,
        p_description: params.description || null,
        p_customer_id: params.customerId || null,
        p_income_category_id: params.incomeCategoryId || null,
        p_cost_center_id: params.costCenterId || null,
        p_financial_account_id: params.financialAccountId || null,
        p_invoice_number: params.invoiceNumber || null,
        p_notes: params.notes || null,
        p_payment_method: params.paymentMethod || null,
        p_issue_date: params.issueDate || null,
      });
      if (error) throw error;
      const result = data as any;
      if (result && !result.success) throw new Error(result.error || 'Erro ao regenerar grupo');
      return result;
    },
    onSuccess: (result) => {
      invalidateAll([['sales'], ['unified-sales'], ['accounts-receivable-by-sale']]);
      toast({ title: `Grupo atualizado: ${result.deleted_count} parcelas removidas, ${result.generated_count} regeneradas` });
    },
    onError: (error) => {
      toast({ title: 'Erro ao atualizar grupo', description: translateSupabaseError(error), variant: 'destructive' });
    },
  });

  // ====================================
  // UPDATE GROUP METADATA ONLY
  // ====================================
  const updateReceivableGroupMetadataMutation = useMutation({
    mutationFn: async (params: {
      groupId: string;
      description?: string;
      customerId?: string;
      incomeCategoryId?: string;
      costCenterId?: string;
      financialAccountId?: string;
      invoiceNumber?: string;
      notes?: string;
      paymentMethod?: string;
    }) => {
      const { data, error } = await supabase.rpc('update_receivable_group_metadata', {
        p_group_id: params.groupId,
        p_description: params.description || null,
        p_customer_id: params.customerId || null,
        p_income_category_id: params.incomeCategoryId || null,
        p_cost_center_id: params.costCenterId || null,
        p_financial_account_id: params.financialAccountId || null,
        p_invoice_number: params.invoiceNumber || null,
        p_notes: params.notes || null,
        p_payment_method: params.paymentMethod || null,
      });
      if (error) throw error;
      return data as any;
    },
    onSuccess: (result) => {
      invalidateAll([['sales'], ['unified-sales'], ['accounts-receivable-by-sale']]);
      toast({ title: `Dados do grupo atualizados (${result.updated_count} parcelas)` });
    },
    onError: (error) => {
      toast({ title: 'Erro ao atualizar dados do grupo', description: translateSupabaseError(error), variant: 'destructive' });
    },
  });

  // ====================================
  // DELETE GROUP
  // ====================================
  const deleteReceivableGroupMutation = useMutation({
    mutationFn: async (groupId: string) => {
      const { data, error } = await supabase.rpc('delete_receivable_group', {
        p_group_id: groupId,
      });
      if (error) throw error;
      const result = data as any;
      if (result && !result.success) throw new Error(result.error || 'Erro ao excluir grupo');
      return result;
    },
    onSuccess: (result) => {
      invalidateAll([['sales'], ['unified-sales'], ['accounts-receivable-by-sale']]);
      toast({
        title: `Grupo excluído: ${result.deleted_count} parcela(s) removida(s)`,
        description: result.preserved_count > 0 ? `${result.preserved_count} parcela(s) com pagamento preservada(s)` : undefined,
      });
    },
    onError: (error) => {
      toast({ title: 'Erro ao excluir grupo', description: translateSupabaseError(error), variant: 'destructive' });
    },
  });

  // ====================================
  // BULK PAY
  // ====================================
  const bulkPayMutation = useMutation({
    mutationFn: async (data: {
      receivables: AccountReceivable[];
      paymentDate: string;
      financialAccountId: string;
      paymentMethod: string;
      notes?: string;
    }) => {
      const eligibles = data.receivables.filter(
        r => !['paid', 'cancelled'].includes(r.status)
      );
      if (eligibles.length === 0) throw new Error('Nenhuma parcela elegível para baixa');

      const failedIds: string[] = [];

      for (const r of eligibles) {
        const paymentValue = Math.max(0, Number(r.total_value) - Number(r.discount_value || 0) - Number(r.paid_value || 0));
        if (paymentValue <= 0) continue;

        try {
          // R-P8.5: RPC dona única da baixa (payment + recomposição do título)
          const { error: rpcError } = await supabase.rpc('register_manual_receivable_payment', {
            p_receivable_id: r.id,
            p_payment_value: paymentValue,
            p_payment_date: data.paymentDate,
            p_payment_method: data.paymentMethod,
            p_financial_account_id: data.financialAccountId,
            p_notes: data.notes || null,
          });
          if (rpcError) throw rpcError;
        } catch (err) {
          console.error(`[bulkPay] Falha na parcela ${r.transaction_number ?? r.id}:`, err);
          failedIds.push(r.transaction_number ?? r.id);
        }
      }

      const paidCount = eligibles.length - failedIds.length;

      // AUDIT-LOG-CHECK (I.160): baixa em lote vai como ATUALIZAÇÃO, evento em details
      await auditService.logAction({
        action: 'ATUALIZAÇÃO',
        module: 'FINANCEIRO',
        entityType: 'accounts_receivable',
        description: `Baixa em lote: ${paidCount} parcela(s) paga(s)${failedIds.length > 0 ? `, ${failedIds.length} com falha: ${failedIds.join(', ')}` : ''}`,
        details: {
          evento: 'baixa_lote',
          paid_count: paidCount,
          failed_count: failedIds.length,
          failed_ids: failedIds,
          payment_method: data.paymentMethod,
          payment_date: data.paymentDate,
          financial_account_id: data.financialAccountId,
        },
      });

      return { count: paidCount, failedIds };
    },
    onSuccess: (data) => {
      invalidateAll([['sales'], ['unified-sales'], ['accounts-receivable-by-sale'], ['bank-transactions']]);
      if (data.failedIds.length > 0) {
        toast({
          title: `${data.count} conta(s) recebida(s) — ${data.failedIds.length} com falha`,
          description: `Parcelas com falha: ${data.failedIds.join(', ')}`,
          variant: 'destructive',
        });
      } else {
        toast({ title: `${data.count} conta(s) recebida(s) com sucesso` });
      }
    },
    onError: (error) => {
      toast({ title: 'Erro no recebimento em lote', description: translateSupabaseError(error), variant: 'destructive' });
    },
  });

  // ====================================
  // BULK DELETE (SOFT)
  // ====================================
  const bulkDeleteReceivablesMutation = useMutation({
    // I.154: cada exclusão passa pela mesma RPC da exclusão unitária (pagamento, cobrança ativa,
    // parcela de venda) — a gravação direta na tabela pulava todas as travas.
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        const { error } = await supabase.rpc('soft_delete_receivable', { p_receivable_id: id });
        if (error) throw error;
      }
      return { count: ids.length };
    },
    onSuccess: (data) => {
      invalidateAll([['sales'], ['unified-sales'], ['accounts-receivable-by-sale']]);
      toast({ title: `${data.count} conta(s) excluída(s) com sucesso` });
    },
    onError: (error) => {
      toast({ title: 'Erro ao excluir contas', description: translateSupabaseError(error), variant: 'destructive' });
    },
  });

  // ====================================
  // BULK RENEGOTIATE
  // ====================================
  const bulkRenegotiateMutation = useMutation({
    mutationFn: async (data: {
      mode: 'reschedule' | 'consolidate';
      rescheduleEntries?: Array<{
        receivableId: string;
        newDueDate: string;
        interestValue: number;
        fineValue: number;
        discountValue: number;
      }>;
      consolidateParams?: {
        receivableIds: string[];
        numberOfInstallments: number;
        firstDueDate: string;
        intervalDays: number;
        totalBalance: number;
        firstReceivable: AccountReceivable;
      };
    }) => {
      if (data.mode === 'reschedule' && data.rescheduleEntries) {
        await Promise.all(data.rescheduleEntries.map(async (entry) => {
          const { data: rec, error: fetchErr } = await supabase
            .from('accounts_receivable')
            .select('original_value, total_value, paid_value, sale_installment_id')
            .eq('id', entry.receivableId)
            .single();
          if (fetchErr || !rec) throw fetchErr ?? new Error('Registro não encontrado');

          const outstanding = (rec.total_value ?? rec.original_value) - (rec.paid_value ?? 0);
          const newTotal = Math.max(0, outstanding + entry.interestValue + entry.fineValue - entry.discountValue);

          const { error: updateErr } = await supabase
            .from('accounts_receivable')
            .update({
              due_date: entry.newDueDate,
              interest_value: entry.interestValue + entry.fineValue,
              discount_value: entry.discountValue,
              total_value: newTotal,
              status: 'renegotiated',
            })
            .eq('id', entry.receivableId)
            .select('id')
            .single();
          if (updateErr) throw updateErr;

          // A4.1 (§I.145 L4): propagar SOMENTE due_date à parcela vinculada —
          // gabarito Camada 1. Valor/status de parcela intocados; título sem
          // vínculo = zero propagação.
          if (rec.sale_installment_id) {
            const { data: instRows, error: instErr } = await supabase
              .from('sale_installments')
              .update({ due_date: entry.newDueDate })
              .eq('id', rec.sale_installment_id)
              .select('id');
            if (instErr) throw instErr;
            if (!instRows || instRows.length === 0) {
              throw new Error('Parcela da venda vinculada a este título não foi atualizada (0 linhas afetadas)');
            }
          }
        }));
        return { count: data.rescheduleEntries.length };
      }

      // Mode B: consolidate — batch renegotiate + N new rows
      const cp = data.consolidateParams!;

      const { data: renegotiatedRows, error: updateErr } = await supabase
        .from('accounts_receivable')
        .update({ status: 'renegotiated' })
        .in('id', cp.receivableIds)
        .select('id');
      if (updateErr) throw updateErr;
      if ((renegotiatedRows?.length ?? 0) !== cp.receivableIds.length) {
        throw new Error(
          `Renegociação parcial: ${renegotiatedRows?.length ?? 0} de ${cp.receivableIds.length} títulos atualizados`
        );
      }

      const installmentValue = Math.floor((cp.totalBalance / cp.numberOfInstallments) * 100) / 100;
      const lastValue = cp.totalBalance - installmentValue * (cp.numberOfInstallments - 1);

      const newRows = await Promise.all(
        Array.from({ length: cp.numberOfInstallments }, async (_, i) => {
          const { data: numData, error: numErr } = await supabase.rpc('generate_next_receivable_number');
          if (numErr) throw numErr;
          const dueDate = new Date(cp.firstDueDate);
          dueDate.setDate(dueDate.getDate() + i * cp.intervalDays);
          const value = i === cp.numberOfInstallments - 1 ? lastValue : installmentValue;
          return {
            company_id: cp.firstReceivable.company_id,
            transaction_number: numData as string,
            customer_id: cp.firstReceivable.customer_id,
            sale_id: cp.firstReceivable.sale_id ?? null,
            rental_id: cp.firstReceivable.rental_id ?? null,
            origin_type: cp.firstReceivable.origin_type ?? null,
            origin_number: cp.firstReceivable.origin_number ?? null,
            income_category_id: (cp.firstReceivable as any).income_category_id ?? null,
            financial_account_id: cp.firstReceivable.financial_account_id ?? null,
            description: `Renegociação Lote - ${cp.firstReceivable.origin_number ?? cp.firstReceivable.transaction_number}`,
            issue_date: format(new Date(), 'yyyy-MM-dd'),
            due_date: format(dueDate, 'yyyy-MM-dd'),
            original_value: value,
            total_value: value,
            paid_value: 0,
            interest_value: 0,
            discount_value: 0,
            status: 'pending',
            // A4.3 (§I.145 L4): sem installment_number — evita colisão no
            // par (sale_id, installment_number) e adoção indevida pelo
            // fallback da regeneração v2; série em total_installments/
            // parent_transaction_id/notes.
            installment_number: null,
            total_installments: cp.numberOfInstallments,
            parent_transaction_id: cp.firstReceivable.id,
            notes: `Parcela ${i + 1}/${cp.numberOfInstallments} - Renegociação em lote de ${cp.receivableIds.length} parcelas`,
          };
        })
      );

      const { error: insertErr } = await supabase.from('accounts_receivable').insert(newRows);
      if (insertErr) throw insertErr;

      return { count: cp.receivableIds.length };
    },
    onSuccess: (data) => {
      invalidateAll([['sales'], ['unified-sales'], ['accounts-receivable-by-sale']]);
      toast({ title: `${data.count} parcela(s) renegociada(s) com sucesso` });
    },
    onError: (error) => {
      toast({ title: 'Erro na renegociação em lote', description: translateSupabaseError(error), variant: 'destructive' });
    },
  });

  // ====================================
  // BULK CHANGE CATEGORY
  // ====================================
  const bulkChangeCategoryMutation = useMutation({
    mutationFn: async ({ ids, categoryId }: { ids: string[]; categoryId: string }) => {
      const { data, error } = await supabase.rpc('bulk_update_receivable_category', {
        p_receivable_ids: ids,
        p_category_id: categoryId,
      });
      if (error) throw error;
      // Trilha de auditoria vem dos triggers de banco (I.128) — sem audit log no client
      return data as unknown as { ar_count: number; revenues_count: number };
    },
    onSuccess: (data) => {
      invalidateAll([['revenues'], ['dre-data-hierarchical'], ['dre-indicators']]);
      toast({
        title: `${data.ar_count} título(s) e ${data.revenues_count} receita(s) reclassificado(s)`,
      });
    },
    onError: (error) => {
      toast({ title: 'Erro ao alterar categoria em lote', description: translateSupabaseError(error), variant: 'destructive' });
    },
  });

  return {
    createReceivable: createMutation.mutate,
    createReceivableAsync: createMutation.mutateAsync,
    createReceivableGroup: createReceivableGroupMutation.mutateAsync,
    updateReceivable: updateMutation.mutateAsync,
    updateReceivableGroup: updateReceivableGroupMutation.mutateAsync,
    updateReceivableGroupMetadata: updateReceivableGroupMetadataMutation.mutateAsync,
    recordPayment: recordPaymentMutation.mutate,
    recordPaymentAsync: recordPaymentMutation.mutateAsync,
    renegotiateReceivable: renegotiateMutation.mutate,
    renegotiateBalance: renegotiateBalanceMutation.mutate,
    reopenReceivable: reopenMutation.mutate,
    revertPaymentEntry: revertPaymentEntryMutation.mutate,
    deleteReceivable: deleteMutation.mutate,
    deleteReceivableGroup: deleteReceivableGroupMutation.mutateAsync,
    bulkPay: bulkPayMutation.mutateAsync,
    bulkDeleteReceivables: bulkDeleteReceivablesMutation.mutateAsync,
    bulkRenegotiate: bulkRenegotiateMutation.mutateAsync,
    bulkChangeCategory: bulkChangeCategoryMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isCreatingGroup: createReceivableGroupMutation.isPending,
    isUpdating: updateMutation.isPending,
    isUpdatingGroup: updateReceivableGroupMutation.isPending || updateReceivableGroupMetadataMutation.isPending,
    isRecordingPayment: recordPaymentMutation.isPending,
    isRenegotiating: renegotiateMutation.isPending,
    isRenegotiatingBalance: renegotiateBalanceMutation.isPending,
    isReopening: reopenMutation.isPending,
    isRevertingPaymentEntry: revertPaymentEntryMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isDeletingGroup: deleteReceivableGroupMutation.isPending,
    isBulkPaying: bulkPayMutation.isPending,
    isBulkDeleting: bulkDeleteReceivablesMutation.isPending,
    isBulkRenegotiating: bulkRenegotiateMutation.isPending,
    isBulkChangingCategory: bulkChangeCategoryMutation.isPending,
  };
};
